import React, { useEffect, useState } from 'react';
import './index.scss';
import { Carousel } from '@/Components/Admission/Main';
import { SupportModal, WaitingListModal } from '@/Components/Admission/Modals';
import { useUniversity } from '@/Recoil/Admission/University';
import { useAdmissionEvent } from '@/Recoil/Admission/Event';
import { useSession } from '@/Recoil/Session/Session';
import { createI18n } from '@/Utils/i18n';
import { languageManager } from '@/Utils/languageManager';
import { navigateTo } from '@/Utils/routing';
import type { Application, Track, University } from 'ai-api/types/admission';
import type { Event } from '@/Data/Admission/Event';
import { listApplicationsAPI } from '@/Data/Admission/Application';
import moment from 'moment-timezone';
import LanguageSelector from '@/Components/LanguageSelector';
import { StudyAbroadPaymentWall } from '@/Components/PaymentWall';
import { usePurchaseHistory } from '@/Recoil/Payment/Purchase';

const translations = {
    'My applications': { id: 'Aplikasi Saya', ko: '내 지원서' },
    'You are editing %d applications': { id: 'Anda sedang mengedit %d aplikasi', ko: '%d개 지원서 작성 중' },
    'No applications': { id: 'Tidak ada aplikasi', ko: '지원서가 없습니다' },
    'Upcoming deadlines': { id: 'Batas waktu mendatang', ko: '마감 임박' },
    'Universities': { id: 'Universitas', ko: '대학교' },
    'There is a MATE who will make your dreams come true.': { id: 'Ada MATE yang akan mewujudkan impian Anda.', ko: 'MATE가 꿈을 이뤄드리겠습니다.' },
    'Get Support': { id: 'Dapatkan Dukungan', ko: '지원 받기' },
    'Privacy Policy': { id: 'Kebijakan Privasi', ko: '개인정보 처리방침' },
    'Terms of Use': { id: 'Syarat Penggunaan', ko: '이용약관' },
    //'All rights reserved.': { id: 'Hak cipta dilindungi.', ko: '모든 권리 보유.' }, // not translated
    'Deadline': { id: 'Batas waktu', ko: '마감일' },
    'View Details': { id: 'Lihat Detail', ko: '자세히 보기' },
    'Start Application': { id: 'Mulai Aplikasi', ko: '지원 시작' },
    'Join Waiting List': { id: 'Bergabung Daftar Tunggu', ko: '대기 명단 등록' },
    'University Waiting List': { id: 'Daftar Tunggu Universitas', ko: '대학 대기 명단' },
    'Full Name': { id: 'Nama Lengkap', ko: '성명' },
    'Email Address': { id: 'Alamat Email', ko: '이메일 주소' },
    'Phone Number': { id: 'Nomor Telepon', ko: '전화번호' },
    'Preferred Program': { id: 'Program Pilihan', ko: '희망 프로그램' },
    'Message': { id: 'Pesan', ko: '메시지' },
    'Submit': { id: 'Kirim', ko: '제출' },
    'Processing...': { id: 'Memproses...', ko: '처리 중...' },
    'Required field': { id: 'Bidang wajib', ko: '필수 입력' },
    'Registration successful': { id: 'Pendaftaran berhasil', ko: '등록 완료' },
    'Registration failed': { id: 'Pendaftaran gagal', ko: '등록 실패' },
    'Support Request': { id: 'Permintaan Dukungan', ko: '지원 요청' },
    'How can we help you?': { id: 'Bagaimana kami bisa membantu Anda?', ko: '어떻게 도와드릴까요?' },
    'Contact Support': { id: 'Hubungi Dukungan', ko: '지원팀 연락' }
};

const t = createI18n(translations);

const Admission: React.FC = () => {
    const { session } = useSession();
    const { hasProduct, error: purchaseError, loading: purchaseLoading } = usePurchaseHistory();
    // Treat fetch errors or loading as no purchase for safety
    const hasPurchased = !purchaseError && !purchaseLoading && hasProduct('mate_study_abroad.250917.01');
    const { universities, tracks, fetchUniversities, fetchTracks } = useUniversity();
    const { events, fetchEvents } = useAdmissionEvent();
    const [applications, setApplications] = useState<Application[]>([]);
    const [, forceUpdate] = useState({});
    const [showWaitingListModal, setShowWaitingListModal] = useState(false);
    const [showSupportModal, setShowSupportModal] = useState(false);
    const [selectedUniversity, setSelectedUniversity] = useState<University | null>(null);
    const [eventsLoading, setEventsLoading] = useState(true);

    // Force re-render when language changes
    useEffect(() => {
        const unsubscribe = languageManager.subscribe(() => forceUpdate({}));
        return unsubscribe;
    }, []);

    // Fetch data on mount
    useEffect(() => {
        const loadData = async () => {
            try {
                // Fetch universities and tracks
                fetchUniversities();
                fetchTracks();
                fetchEvents().then(() => setEventsLoading(false));

                // Fetch user's applications if logged in
                if (session?.userid) {
                    try {
                        const apps = await listApplicationsAPI({});
                        if (apps) {
                            setApplications(apps);
                        }
                    } catch (error) {
                        console.error('Failed to fetch applications:', error);
                    }
                }
            } catch (error) {
                console.error('Error loading admission data:', error);
            }
        };

        loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session?.userid]);

    // Convert events to carousel items
    // Filter for active events that should be featured (you can add more logic here)
    const getCarouselItems = () => {
        if (eventsLoading) {
            return [];
        }

        const featuredEvents = Object.values(events)
            .filter(event => event.status === 'active')
            .sort((a, b) => b.created_at - a.created_at) // Most recent first
            .slice(0, 5); // Take top 5 events

        if (featuredEvents.length === 0) {
            return [];
        }

        return featuredEvents.map(event => ({
            // Use event banner image if available, otherwise generate based on event type
            image: getEventBannerImage(event),
            // Navigate to event detail or registration page
            destination: event.online_link
        }));
    };

    // Helper function to get event banner image
    const getEventBannerImage = (event: Event) => {
        // If event has image_url or banner_url, use it
        if (event.image_url) {
            return event.image_url;
        }
        if (event.banner_url) {
            return event.banner_url;
        }

        // Otherwise, use default images based on event type
        const eventTypeImages: Record<string, string> = {
            info_session: 'https://static.koreanmate.app/admission/event-info-session.jpg',
            entrance_exam: 'https://static.koreanmate.app/admission/event-exam.jpg',
            interview: 'https://static.koreanmate.app/admission/event-interview.jpg',
            consultation: 'https://static.koreanmate.app/admission/event-consultation.jpg',
            deadline: 'https://static.koreanmate.app/admission/event-deadline.jpg',
            announcement: 'https://static.koreanmate.app/admission/event-announcement.jpg',
            other: 'https://static.koreanmate.app/admission/event-default.jpg'
        };

        return eventTypeImages[event.type] || eventTypeImages.other;
    };

    const carouselItems = getCarouselItems();

    // Get tracks with upcoming deadlines
    const getUpcomingDeadlineTracks = (): [number, Track[]][] => {
        const start = moment();
        const oneMonthFromStart = start.clone().add(1, 'month');

        let ret = [];
        for (let now = start.clone(); now < oneMonthFromStart; now.add(1, 'day')) {
            const dayStart = now.clone().startOf('day').unix();
            const dayEnd = now.clone().endOf('day').unix();

            let row = Object.values(tracks)
                .filter(track =>
                    track.application_deadline >= dayStart &&
                    track.application_deadline <= dayEnd &&
                    track.status === 'active'
                )
                .sort((a, b) => a.application_deadline - b.application_deadline);
                
            if (row.length > 0) {
                ret.push([dayEnd, row] as [number, Track[]]);
            }
        }
        return ret;
    };

    const formatDate = (timestamp: number): string => {
        return new Date(timestamp * 1000).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const upcomingTracks = getUpcomingDeadlineTracks();

    // Handle university card click
    const handleUniversityClick = (univ: University) => {
        if ((!univ.description || univ.description.trim() === '')
            && (!univ.introduction || univ.introduction.trim() === '')) {
            // Open waiting list modal for universities without description
            setSelectedUniversity(univ);
            setShowWaitingListModal(true);
            // Only push state for desktop (not fullscreen modal)
            if (window.innerWidth > 800) {
                window.history.pushState({ modalOpen: true }, '', window.location.href);
            }
        } else {
            // Navigate to university page for universities with description
            navigateTo(`/admission/${univ.universityid}`);
        }
    };

    // Close modal
    const closeModal = () => {
        setShowWaitingListModal(false);
        setSelectedUniversity(null);
        // Only handle history for desktop (not fullscreen modal)
        if (window.innerWidth > 800 && window.history.state?.modalOpen) {
            window.history.back();
        }
    };

    const closeSupportModal = () => {
        setShowSupportModal(false);
        if (window.history.state?.modalOpen) {
            window.history.back();
        }
    };

    // Handle modal close on ESC and back button
    useEffect(() => {
        const handlePopState = (e: PopStateEvent) => {
            // Only handle popstate for desktop (not fullscreen modal)
            if (window.innerWidth > 800 && !e.state?.modalOpen && showWaitingListModal) {
                setShowWaitingListModal(false);
                setSelectedUniversity(null);
            }
        };

        const handleEscKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && showWaitingListModal) {
                closeModal();
            }
        };

        // For mobile, intercept back button to just close modal
        const handleBackButtonMobile = (e: PopStateEvent) => {
            if (window.innerWidth <= 800 && showWaitingListModal) {
                e.preventDefault();
                closeModal();
                // Push state again to keep intercepting back button
                window.history.pushState({ mobileModal: true }, '', window.location.href);
            }
        };

        if (window.innerWidth > 800) {
            // Desktop behavior
            window.addEventListener('popstate', handlePopState);
        } else if (showWaitingListModal) {
            // Mobile behavior - push state and intercept back
            window.history.pushState({ mobileModal: true }, '', window.location.href);
            window.addEventListener('popstate', handleBackButtonMobile);
        }

        if (showWaitingListModal) {
            window.addEventListener('keydown', handleEscKey);
        }

        return () => {
            window.removeEventListener('popstate', handlePopState);
            window.removeEventListener('popstate', handleBackButtonMobile);
            window.removeEventListener('keydown', handleEscKey);
        };
    }, [showWaitingListModal]);

    return (
        <div className={`Admission ${!hasPurchased ? 'payment-restricted' : ''}`}>
            <StudyAbroadPaymentWall productid="mate_study_abroad.250917.01" />
            <div className="CarouselContainer">
                {eventsLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', backgroundColor: '#f5f5f5' }}>
                        <p>Loading...</p>
                    </div>
                ) : (
                    carouselItems.length > 0 && <Carousel className={"Carousel"} items={carouselItems} />
                )}
            </div>

            <div className="Container">
                {/* My Applications Section */}
                {session?.userid && (
                    <div className="MyApplications">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h2>{t('My applications')}</h2>
                            <LanguageSelector onChange={(newLang) => {
                                languageManager.setLanguage(newLang as any);
                                window.location.reload();
                            }} />
                        </div>
                        <div className="Summary">
                            <p>
                                {applications.length > 0
                                    ? t('You are editing %d applications').replace('%d', applications.length.toString())
                                    : t('No applications')}
                            </p>
                        </div>
                        <div className="Applications HorizontalScroll">
                            {applications.map(app => (
                                <div
                                    className="ApplicationCard"
                                    key={app.applicationid}
                                    onClick={() => navigateTo(`/admission/${app.universityid}/${app.trackid}`)}
                                >
                                    <div className="UniversityName">
                                        {universities[app.universityid]?.name || app.universityid}
                                    </div>
                                    <div className="TrackName">
                                        {tracks[app.trackid]?.name || app.trackid}
                                    </div>
                                    <div className="Status">{app.status}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Upcoming Deadlines Section */}
                {upcomingTracks.length > 0 && (
                    <div className="UpcomingDeadlines">
                        <h2>{t('Upcoming deadlines')}</h2>
                        <div className="DeadlinesList">
                            {upcomingTracks.map(([deadline, tracks]) => {
                                const deadlineDate = moment(deadline * 1000).format('DD.');
                                
                                return (
                                    <div
                                        className="DeadlineItem"
                                        key={deadlineDate}
                                    >
                                        <div className="DateCircle">
                                            <span>{deadlineDate}</span>
                                        </div>
                                        <div className="Tracks">
                                            {tracks.map(track => (
                                                <div className='Track' onClick={(()=> {
                                                    navigateTo(`/admission/${track.universityid}/${track.trackid}`);
                                                })}>
                                                    <img
                                                        className="UniversityLogo"
                                                        src={universities[track.universityid]?.logo_url || 'https://via.placeholder.com/24'}
                                                        alt=""
                                                    />
                                                    <div className="DeadlineInfo">
                                                        <span className="UniversityName">{universities[track.universityid]?.name}</span>
                                                        <span className="Date">{formatDate(track.application_deadline)} {track.name}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Universities Section */}
                <div className="Universities">
                    <h2>{t('Universities')}</h2>
                    <div className="TwoColumns">
                        {Object.values(universities).map(univ => (
                            <div
                                className="UniversityCard"
                                key={univ.universityid}
                                onClick={() => handleUniversityClick(univ)}
                            >
                                <div className="UniversityLogo">
                                    {univ.logo_url && (
                                        <img src={univ.logo_url} alt={univ.name} />
                                    )}
                                </div>
                                <div className="UniversityInfo">
                                    <div className="UniversityName">{univ.name}</div>
                                    <div className="Location">{univ.location}</div>
                                    <div className="Description">
                                        {(univ.description || univ.introduction || "")?.substring(0, 100) + '...'}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Support Section */}
                <div className="Support">
                    <p>{t('There is a MATE who will make your dreams come true.')}</p>
                    <button onClick={() => {
                        setShowSupportModal(true);
                        window.history.pushState({ modalOpen: true }, '', window.location.href);
                    }}>
                        {t('Get Support')}
                    </button>
                </div>

                {/* Footer */}
                <footer className="Footer">
                    <p>{`© ${new Date().getFullYear()} WEBTOON TODAY`}</p>
                    <p>{"Korea: WEBTOON TODAY | Indonesia: KOREAN CULTURE MATE"}</p>
                    <p>{t('All rights reserved.')}</p>
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
                        <a href="/privacypolicy">
                            <p>{t('Privacy Policy')}</p>
                        </a>
                        <a href="/termsofuse">
                            <p>{t('Terms of Use')}</p>
                        </a>
                    </div>
                    <a href="mailto:help@koreanmate.app" target="_blank" rel="noreferrer">
                        <p>{"📧help@koreanmate.app"}</p>
                    </a>
                    <a
                        href="https://x.com/@webtoon_today"
                        target="_blank"
                        rel="noreferrer"
                        style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}
                    >
                        <img
                            style={{ width: 20, height: 20 }}
                            src="https://static.koreanmate.app/outlink/x.png"
                            alt="x"
                        />
                        <p>{"Mate by @webtoon_today"}</p>
                    </a>
                </footer>
            </div>

            {/* Support Modal */}
            <SupportModal
                isOpen={showSupportModal}
                onClose={closeSupportModal}
            />

            {/* Waiting List Modal */}
            <WaitingListModal
                isOpen={showWaitingListModal}
                onClose={closeModal}
                university={selectedUniversity}
            />
        </div>
    );
};

export default Admission;
